import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import {
  Linkedin, Chrome, Download, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Wifi, WifiOff, Shield, Clock, Zap, ExternalLink,
  Info, Loader2, ArrowRight, Settings
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function LinkedInExtensionSetup() {
  const [extensionStatus, setExtensionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, usageRes, settingsRes] = await Promise.all([
        api.get('/api/linkedin/extension/status').catch(() => ({ data: { status: 'not_installed' } })),
        api.get('/api/linkedin/usage/today').catch(() => ({ data: null })),
        api.get('/api/linkedin/settings').catch(() => ({ data: null })),
      ]);
      setExtensionStatus(statusRes.data);
      setUsage(usageRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Failed to load extension status:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const res = await api.put('/api/linkedin/settings', {
        daily_connect_limit: Number(settings.daily_connect_limit),
        daily_message_limit: Number(settings.daily_message_limit),
        autonomy: settings.autonomy,
        connect_note_mode: settings.connect_note_mode || 'with_note',
        ai_reply_mode: settings.ai_reply_mode || 'draft',
      });
      setSettings(s => ({ ...s, ...res.data }));
      toast.success('LinkedIn settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const statusColor = {
    connected: 'text-emerald-500',
    disconnected: 'text-amber-500',
    not_installed: 'text-muted-foreground',
  };

  const statusIcon = {
    connected: Wifi,
    disconnected: WifiOff,
    not_installed: Chrome,
  };

  const StatusIcon = statusIcon[extensionStatus?.status] || Chrome;

  return (
    <div className="space-y-6" data-testid="extension-setup-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Chrome className="h-6 w-6 text-sky-500" />
          LinkedIn Extension Setup
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Install the Chrome extension to automate LinkedIn outreach directly from your browser.
        </p>
      </div>

      {/* Connection Status */}
      <Card data-testid="extension-status-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${statusColor[extensionStatus?.status] || 'text-muted-foreground'}`} />
              Extension Status
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={loadStatus} disabled={loading} data-testid="refresh-status-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking status...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={`px-3 py-1 ${
                    extensionStatus?.status === 'connected'
                      ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                      : extensionStatus?.status === 'disconnected'
                      ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                      : 'bg-muted'
                  }`}
                  data-testid="extension-status-badge"
                >
                  {extensionStatus?.status === 'connected' && <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                  {extensionStatus?.status === 'disconnected' && <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />}
                  {extensionStatus?.status === 'not_installed' && <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                  {extensionStatus?.status === 'connected' ? 'Connected'
                    : extensionStatus?.status === 'disconnected' ? 'Disconnected'
                    : 'Not Installed'}
                </Badge>
                {extensionStatus?.last_heartbeat && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last seen: {new Date(extensionStatus.last_heartbeat).toLocaleTimeString()}
                  </span>
                )}
              </div>

              {/* Usage (if connected) */}
              {usage && extensionStatus?.status === 'connected' && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="border rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-sky-500">{usage.connects_today}</p>
                    <p className="text-xs text-muted-foreground">Connects Today /{usage.daily_connect_limit}</p>
                  </div>
                  <div className="border rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-sky-500">{usage.messages_today}</p>
                    <p className="text-xs text-muted-foreground">Messages Today /{usage.daily_message_limit}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automation Settings */}
      {settings && (
        <Card data-testid="linkedin-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Outreach Settings
            </CardTitle>
            <CardDescription>
              Per-workspace daily limits and reply autonomy. Hard safety ceilings apply ({settings.max_connect_limit} connects / {settings.max_message_limit} messages).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Daily connect limit</label>
                <input
                  type="number" min={1} max={settings.max_connect_limit}
                  value={settings.daily_connect_limit}
                  onChange={(e) => setSettings(s => ({ ...s, daily_connect_limit: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="settings-connect-limit-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Daily message limit</label>
                <input
                  type="number" min={1} max={settings.max_message_limit}
                  value={settings.daily_message_limit}
                  onChange={(e) => setSettings(s => ({ ...s, daily_message_limit: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="settings-message-limit-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">AI reply autonomy</label>
                <select
                  value={settings.autonomy || 'semi_auto'}
                  onChange={(e) => setSettings(s => ({ ...s, autonomy: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="settings-autonomy-select"
                >
                  <option value="semi_auto">Semi-auto — I approve AI replies</option>
                  <option value="full_auto">Full-auto — send AI replies hands-free</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Connection request note</label>
              <select
                value={settings.connect_note_mode || 'with_note'}
                onChange={(e) => setSettings(s => ({ ...s, connect_note_mode: e.target.value }))}
                className="w-full sm:w-1/2 h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="settings-note-mode-select"
              >
                <option value="with_note">With personalized note (recommended — higher acceptance)</option>
                <option value="without_note">Without note (faster — skips the note step)</option>
              </select>
              <p className="text-[11px] text-muted-foreground">
                Applies to new AND already-queued connect tasks. Notes stay saved — switch back anytime to restore them.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">AI reply mode (inbound LinkedIn messages)</label>
              <select
                value={settings.ai_reply_mode || 'draft'}
                onChange={(e) => setSettings(s => ({ ...s, ai_reply_mode: e.target.value }))}
                className="w-full sm:w-1/2 h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="settings-ai-reply-mode-select"
              >
                <option value="draft">Draft — Leo pre-writes a reply for you to approve (recommended)</option>
                <option value="off">Off — Leo never drafts LinkedIn replies</option>
                <option value="auto" disabled>Auto — send hands-free (coming soon)</option>
              </select>
              <p className="text-[11px] text-muted-foreground" data-testid="settings-ai-reply-mode-hint">
                Hands-free auto-send is intentionally locked while we verify safe LinkedIn limits — it protects your account from automation flags.
              </p>
            </div>
            <p className="text-[11px] text-muted-foreground">
              New LinkedIn accounts should warm up gradually (5-10 connects/day for the first week).
              Each executed connect/message costs 0.5 credits.
            </p>
            <Button size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="settings-save-btn">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Installation Steps */}
      <Card data-testid="installation-steps-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Installation Guide
          </CardTitle>
          <CardDescription>
            Follow these steps to set up the LinkedIn outreach extension
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full bg-sky-500/10 text-sky-500 font-bold flex items-center justify-center text-sm">1</div>
            <div>
              <h4 className="font-semibold text-sm">Install the Extension</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Install the Lockene LinkedIn Outreach extension from the Chrome Web Store.
                It's unlisted — use the direct link below.
              </p>
              <Button className="mt-3 gap-2" size="sm" data-testid="install-extension-btn" asChild>
                <a href="https://chromewebstore.google.com/detail/hichhoddldjhjkdpohjpfbljjefnlnoh" target="_blank" rel="noopener noreferrer">
                  <Chrome className="h-4 w-4" />
                  Install from Chrome Web Store
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>

          <Separator />

          {/* Step 2 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full bg-sky-500/10 text-sky-500 font-bold flex items-center justify-center text-sm">2</div>
            <div>
              <h4 className="font-semibold text-sm">Sign In with Your Lockene Account</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Click the extension icon in Chrome's toolbar and sign in with the same email and password you use for Lockene CRM.
                The extension uses your existing account — no separate API key needed.
              </p>
            </div>
          </div>

          <Separator />

          {/* Step 3 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full bg-sky-500/10 text-sky-500 font-bold flex items-center justify-center text-sm">3</div>
            <div>
              <h4 className="font-semibold text-sm">Open LinkedIn & Toggle On</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Open linkedin.com in a tab. Then toggle the extension to "Running" in the popup.
                The extension will automatically pick up and execute your pending outreach tasks.
              </p>
            </div>
          </div>

          <Separator />

          {/* Step 4 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full bg-sky-500/10 text-sky-500 font-bold flex items-center justify-center text-sm">4</div>
            <div>
              <h4 className="font-semibold text-sm">Generate Tasks in Lockene</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Go to any lead's detail page and click "Generate" or "Sequence" to create outreach tasks.
                The extension will handle the rest — sending connection requests and messages with human-like timing.
              </p>
              <Button variant="outline" className="mt-3 gap-2" size="sm"
                onClick={() => window.location.href = '/linkedin-outbox'} data-testid="go-to-outbox-btn">
                Go to LinkedIn Outbox <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Safety & Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Safety & Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Human-Speed Throttling</AlertTitle>
            <AlertDescription className="text-xs">
              The extension mimics human behavior with 3-8 minute delays between actions and random typing speeds.
              Daily limits are enforced: ~22 connections/day and ~50 messages/day.
            </AlertDescription>
          </Alert>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>LinkedIn Account Safety</AlertTitle>
            <AlertDescription className="text-xs">
              While the extension uses human-like patterns, automated LinkedIn activity carries some risk.
              We recommend starting with low volume (5/day for the first week) and gradually increasing.
              The extension auto-pauses if a LinkedIn restriction is detected.
            </AlertDescription>
          </Alert>
          <Alert>
            <Zap className="h-4 w-4" />
            <AlertTitle>Smart Self-Healing</AlertTitle>
            <AlertDescription className="text-xs">
              If LinkedIn changes their UI, the extension automatically fetches updated selectors from our servers.
              Most DOM changes are fixed within hours without needing an extension update.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
